import { useState } from 'react';
import { DndContext, useDraggable, useDroppable, type DragEndEvent } from '@dnd-kit/core';
import { Grip } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useThemeStore, type SidebarPlacement } from '@/lib/theme/store';

const PLACEMENTS: SidebarPlacement[] = ['left', 'right', 'top'];

const ZONE_LABELS: Record<SidebarPlacement, string> = {
  left: 'Dock left',
  right: 'Dock right',
  top: 'Dock top',
};

/**
 * Provides drag-to-dock for the sidebar. Wraps the AppShell so the entire
 * layout participates in the drag context. When the user grabs the drag
 * handle and drops on an edge zone, the sidebar placement is updated in
 * the theme store and persisted.
 */
export function SidebarDocker({ children }: { children: React.ReactNode }) {
  const currentPlacement = useThemeStore((s) => s.sidebarPlacement);
  const setPlacement = useThemeStore((s) => s.setSidebarPlacement);
  const [isDragging, setIsDragging] = useState(false);

  const handleEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    const overId = event.over?.id;
    if (typeof overId === 'string' && PLACEMENTS.includes(overId as SidebarPlacement)) {
      setPlacement(overId as SidebarPlacement);
    }
  };

  return (
    <DndContext onDragStart={() => setIsDragging(true)} onDragCancel={() => setIsDragging(false)} onDragEnd={handleEnd}>
      {children}
      <DockOverlay active={isDragging} currentPlacement={currentPlacement} />
    </DndContext>
  );
}

/**
 * Grab handle rendered inside the sidebar. Wires up the dnd-kit draggable
 * for the dock-zone drop targets owned by <SidebarDocker>.
 */
export function SidebarDragHandle() {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: 'sidebar-drag-handle' });
  return (
    <button
      ref={setNodeRef}
      type="button"
      className="inline-flex h-8 w-8 cursor-grab items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground active:cursor-grabbing"
      aria-label="Drag sidebar to dock to a different edge"
      {...attributes}
      {...listeners}
    >
      <Grip className="h-4 w-4" />
    </button>
  );
}

function DockOverlay({
  active,
  currentPlacement,
}: {
  active: boolean;
  currentPlacement: SidebarPlacement;
}) {
  if (!active) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[100]">
      {PLACEMENTS.filter((p) => p !== currentPlacement).map((placement) => (
        <DockZone key={placement} placement={placement} />
      ))}
    </div>
  );
}

function DockZone({ placement }: { placement: SidebarPlacement }) {
  const { isOver, setNodeRef } = useDroppable({ id: placement });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'pointer-events-auto absolute flex items-center justify-center border-2 border-dashed border-primary/60 bg-primary/10 backdrop-blur-sm transition-colors',
        'text-sm font-semibold uppercase tracking-wider text-primary',
        placement === 'left' && 'left-0 top-0 h-screen w-48',
        placement === 'right' && 'right-0 top-0 h-screen w-48',
        placement === 'top' && 'left-0 right-0 top-0 h-32',
        isOver && 'border-primary bg-primary/25',
      )}
    >
      {ZONE_LABELS[placement]}
    </div>
  );
}
